import { t } from "@lingui/macro";
import { CircleNotchIcon, CoinIcon, CreditCardIcon, ShoppingCartIcon } from "@phosphor-icons/react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from "@reactive-resume/ui";
import { useState } from "react";

import { useCrBalance } from "@/client/services/credit/balance";
import { useUser } from "@/client/services/user";
import { useDialog } from "@/client/stores/dialog";

type CreditAlertType = "creation" | "download" | "ats" | "translate" | "improve";

type AiPackReason = "scoring" | "translate" | "improve";

type AlertContent = {
  title: string;
  message: string;
  advice: string;
  button: string;
};

const getAlertContent = (type?: CreditAlertType): AlertContent => {
  switch (type) {
    case "ats": {
      return {
        title: "Crédits IA insuffisants",
        message: "Votre solde ne permet pas de lancer cette analyse ATS.",
        advice:
          "Choisissez un pack de crédits IA puis reprenez votre analyse avec le nouveau solde.",
        button: "Recharger mes crédits IA",
      };
    }

    case "translate": {
      return {
        title: "Crédits IA insuffisants",
        message: "Votre solde ne permet pas de traduire l’intégralité de ce CV.",
        advice: "La traduction est facturée selon les tokens réellement consommés.",
        button: "Recharger mes crédits IA",
      };
    }

    case "improve": {
      return {
        title: "Crédits IA insuffisants",
        message: "Votre solde ne permet pas de générer cette suggestion.",
        advice: "Rechargez votre portefeuille IA pour poursuivre l’amélioration du CV.",
        button: "Recharger mes crédits IA",
      };
    }

    case "download": {
      return {
        title: "Téléchargements épuisés",
        message: "Vous avez utilisé tous vos crédits de téléchargement disponibles.",
        advice: t`Chaque export PDF (.pdf) consomme un crédit de téléchargement provenant de votre offre CV.`,
        button: "Découvrir les offres CV",
      };
    }

    default: {
      return {
        title: "Solde insuffisant",
        message: "Votre solde ne permet pas d’effectuer cette action.",
        advice: "Choisissez la formule correspondant à l’action que vous souhaitez réaliser.",
        button: "Voir les formules",
      };
    }
  }
};

const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message;
  }

  return "Impossible de vérifier votre solde de téléchargements. Veuillez réessayer.";
};

const getAiPackReason = (type?: CreditAlertType): AiPackReason => {
  if (type === "ats") {
    return "scoring";
  }

  if (type === "translate") {
    return "translate";
  }

  return "improve";
};

export const AlertCreditBalance = () => {
  const { isOpen, close, payload } = useDialog<{
    type?: CreditAlertType;
  }>("alert-credit-balance");

  const { open: openCvOffers } = useDialog("forfait");

  const { open: openAiPacks } = useDialog("ai-packs");

  const { user } = useUser();

  const { refetch: refreshDownloadBalance, refreshing } = useCrBalance(user?.id);

  const [purchaseError, setPurchaseError] = useState<string | null>(null);

  const type = payload?.item?.type;

  const content = getAlertContent(type);

  const isAiAction = type === "ats" || type === "translate" || type === "improve";

  const openCvOffersDialog = () => {
    close();

    openCvOffers("create", {
      id: "forfait",
      item: {
        type: type === "download" ? "download" : "creation",
      },
    });
  };

  const openAiPacksDialog = () => {
    const reason = getAiPackReason(type);

    close();

    openAiPacks("create", {
      id: "ai-packs",
      item: {
        type: reason,
      },
    });
  };

  const startPurchase = async () => {
    if (refreshing) {
      return;
    }

    setPurchaseError(null);

    /*
     * Une demande de création ou de
     * téléchargement ouvre directement
     * les offres CV.
     */
    if (!isAiAction) {
      openCvOffersDialog();
      return;
    }

    if (!user?.id) {
      setPurchaseError("Votre session est introuvable. Veuillez vous reconnecter.");

      return;
    }

    try {
      /*
       * La valeur est relue depuis le serveur
       * au moment exact du clic.
       *
       * On ne prend donc pas la décision avec
       * une ancienne valeur React Query.
       */
      const result = await refreshDownloadBalance();

      if (result.isError) {
        throw result.error;
      }

      const remainingDownloads = Number(result.data?.credit ?? 0);

      /*
       * Sans téléchargement CV disponible,
       * l’utilisateur doit commencer par
       * acheter une offre CV.
       */
      if (remainingDownloads <= 0) {
        openCvOffersDialog();
        return;
      }

      /*
       * Avec au moins un téléchargement CV,
       * l’utilisateur peut acheter un pack IA.
       */
      openAiPacksDialog();
    } catch (error: unknown) {
      setPurchaseError(getErrorMessage(error));
    }
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          setPurchaseError(null);
          close();
        }
      }}
    >
      <DialogContent className="w-[calc(100vw-2rem)] max-w-md rounded-2xl border-0 p-5 shadow-2xl sm:p-6">
        <div className="flex flex-col items-center gap-4 pb-2 pt-3 text-center">
          <div className="relative grid size-16 place-items-center rounded-full bg-rose-50 text-rose-500">
            <CreditCardIcon aria-hidden className="size-8" />

            <span className="absolute -bottom-0.5 -right-0.5 grid size-6 place-items-center rounded-full border-2 border-white bg-rose-500 text-[10px] font-bold text-white">
              !
            </span>
          </div>

          <DialogTitle className="text-xl font-bold text-[#071451]">{content.title}</DialogTitle>

          <DialogDescription className="text-sm leading-6 text-slate-600">
            {content.message}
          </DialogDescription>
        </div>

        <div className="mt-3 flex items-start gap-3 rounded-xl border border-indigo-100 bg-indigo-50/60 p-4">
          <span className="grid size-9 shrink-0 place-items-center rounded-full border border-indigo-200 bg-white text-indigo-600">
            <CoinIcon aria-hidden className="size-5" />
          </span>

          <div>
            <h2 className="text-xs font-bold text-indigo-800">Conseil</h2>

            <p className="mt-1 text-xs leading-5 text-slate-600">{content.advice}</p>
          </div>
        </div>

        {purchaseError && (
          <div
            role="alert"
            className="mt-3 rounded-xl border border-rose-200 bg-rose-50 px-3.5 py-2.5 text-xs font-semibold leading-5 text-rose-700"
          >
            {purchaseError}
          </div>
        )}

        <DialogFooter className="mt-5">
          <Button
            type="button"
            disabled={refreshing}
            onClick={() => {
              void startPurchase();
            }}
            className="min-h-11 w-full rounded-xl bg-gradient-to-r from-indigo-700 via-blue-600 to-emerald-500 px-5 text-xs font-bold text-white sm:text-sm"
          >
            {refreshing ? (
              <>
                <CircleNotchIcon aria-hidden size={18} weight="bold" className="animate-spin" />
                Vérification du forfait…
              </>
            ) : (
              <>
                <ShoppingCartIcon aria-hidden size={18} />

                {content.button}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
